/**
 * 配色預設。
 *
 * ★ 這個檔案由 `node scripts/build-palettes.mjs` 產生，不要手改。
 *   配色卡只給「主色 + 襯色」兩個值，網頁需要九個，而且每個都要通過
 *   對比度門檻。十七組手算會出錯，所以由腳本推導並自我檢查。
 *
 * 來源：專案負責人提供的配色卡（小紅書「神仙配色／設計芸卷」與 XIAOXINDESIGN）。
 *
 * 設計約束（規範第十二節）：
 *   - 學生只能從這些預設裡挑。不開放任意顏色輸入，因為任意輸入必然
 *     產生不可讀的組合。
 *   - **正文對比 ≥7:1（AAA），UI 文字與連結 ≥4.5:1（AA）。**
 *     由 packages/themes/test/palettes.test.ts 機械檢查。
 *   - `accent` 是配色卡原色，**只用於裝飾**；文字與連結一律用推導出來的
 *     `link`（壓暗到達標為止）。海報色多半中高明度，直接當文字色讀不清。
 *
 * 回覆區用 `commentSurface`（襯色的淡化版），與正文的 `surface` 分開，
 * 讓讀者一眼分辨「哪些是作者寫的、哪些是別人回的」。
 */

export interface Palette {
  /** 穩定 id，存進 theme JSON */
  id: string;
  /** 顯示名稱（配色卡上的中文名） */
  name: string;
  /** 主色（配色卡原色）。只用於裝飾，不當文字色。 */
  accent: string;
  /** 由 accent 壓暗派生、保證 ≥4.5:1 的文字／連結色 */
  link: string;
  /** 襯色的淡化版 */
  wash: string;
  /** 頁面底色 */
  background: string;
  /** 內容卡片底色 */
  surface: string;
  /** ★ 回覆區底色——必須與 surface 有可見差異 */
  commentSurface: string;
  /** 正文墨色 */
  text: string;
  /** 次要文字 */
  textMuted: string;
  /** 分隔線 */
  border: string;
}

export const PALETTES: readonly Palette[] = [
  {
    id: "yutanqing",
    name: "雨潭青",
    accent: "#63BBD0",
    link: "#3D7481",
    wash: "#FFE6E3",
    background: "#FFF8F7",
    surface: "#FFFFFF",
    commentSurface: "#FFEEEB",
    text: "#243133",
    textMuted: "#5C6D72",
    border: "#EADEDD",
  },
  {
    id: "yuhonglan",
    name: "雨泓藍",
    accent: "#63BBD0",
    link: "#3D7481",
    wash: "#FFEEC0",
    background: "#FFFAED",
    surface: "#FFFFFF",
    commentSurface: "#FFF1CA",
    text: "#243133",
    textMuted: "#5C6D72",
    border: "#EAE2CD",
  },
  {
    id: "jianyunbao",
    name: "澗筠薄",
    accent: "#83A78D",
    link: "#597260",
    wash: "#F6EAA9",
    background: "#FCF9E7",
    surface: "#FFFFFF",
    commentSurface: "#F9F2CB",
    text: "#292E2A",
    textMuted: "#626964",
    border: "#E6E0C3",
  },
  {
    id: "jiangliuzhuo",
    name: "絳榴灼",
    accent: "#EE1969",
    link: "#D1165C",
    wash: "#F9E5E0",
    background: "#FDF8F6",
    surface: "#FFFFFF",
    commentSurface: "#FBEFEC",
    text: "#381A25",
    textMuted: "#784D5D",
    border: "#E7DEDC",
  },
  {
    id: "wuaizi",
    name: "霧靄紫",
    accent: "#615EA8",
    link: "#615EA8",
    wash: "#ECECF5",
    background: "#FAFAFC",
    surface: "#FFFFFF",
    commentSurface: "#F6F5FA",
    text: "#24242E",
    textMuted: "#5B5B6A",
    border: "#E1E1E5",
  },
  {
    id: "danshenqian",
    name: "丹椹淺",
    accent: "#FC8A3A",
    link: "#A15825",
    wash: "#C4F1FF",
    background: "#EEFBFF",
    surface: "#FFFFFF",
    commentSurface: "#DAF6FF",
    text: "#3A2A1E",
    textMuted: "#7A6454",
    border: "#CFE3EA",
  },
  {
    id: "maerselv",
    name: "馬爾斯綠",
    accent: "#01847F",
    link: "#017975",
    wash: "#FBE2EE",
    background: "#FEF7FA",
    surface: "#FFFFFF",
    commentSurface: "#FDEDF4",
    text: "#172928",
    textMuted: "#486261",
    border: "#E8DDE2",
  },
  {
    id: "zhusha",
    name: "朱砂",
    accent: "#D30121",
    link: "#D30121",
    wash: "#D7EBFF",
    background: "#F4F9FF",
    surface: "#FFFFFF",
    commentSurface: "#E8F3FF",
    text: "#34171B",
    textMuted: "#72484F",
    border: "#D8E1EA",
  },
  {
    id: "qingshuilan",
    name: "清水藍",
    accent: "#3F389F",
    link: "#3F389F",
    wash: "#FDE3E2",
    background: "#FEF7F7",
    surface: "#FFFFFF",
    commentSurface: "#FEEFEE",
    text: "#1F1E2D",
    textMuted: "#555368",
    border: "#E9DDDD",
  },
  {
    id: "diwangzi",
    name: "帝王紫",
    accent: "#6A0DAD",
    link: "#6A0DAD",
    wash: "#FFF000",
    background: "#FFFBB8",
    surface: "#FFFFFF",
    commentSurface: "#FFF661",
    text: "#25182F",
    textMuted: "#5D4B6B",
    border: "#EAE377",
  },
  {
    id: "qinglan",
    name: "青藍",
    accent: "#00B7C7",
    link: "#007983",
    wash: "#D6F3F6",
    background: "#F4FCFC",
    surface: "#FFFFFF",
    commentSurface: "#E6F8F9",
    text: "#163032",
    textMuted: "#486D70",
    border: "#D7E4E6",
  },
  {
    id: "tailv",
    name: "苔綠",
    accent: "#4D613C",
    link: "#4D613C",
    wash: "#F9E4E9",
    background: "#FDF7F9",
    surface: "#FFFFFF",
    commentSurface: "#FCEFF2",
    text: "#21241F",
    textMuted: "#575B54",
    border: "#E7DDE0",
  },
  {
    id: "qingque",
    name: "青雀頭黛",
    accent: "#153C46",
    link: "#153C46",
    wash: "#FDE8AD",
    background: "#FEF9E8",
    surface: "#FFFFFF",
    commentSurface: "#FEF1CC",
    text: "#191F20",
    textMuted: "#4C5456",
    border: "#E9DFC5",
  },
  {
    id: "hailan",
    name: "海藍",
    accent: "#1E9BFF",
    link: "#1670B8",
    wash: "#F0FF0A",
    background: "#FBFFBA",
    surface: "#FFFFFF",
    commentSurface: "#F0FF0A",
    text: "#1B2C3A",
    textMuted: "#4E677B",
    border: "#E3EA7B",
  },
  {
    id: "zhuanhezong",
    name: "磚褐棕",
    accent: "#8D4726",
    link: "#8D4726",
    wash: "#C3F2ED",
    background: "#EEFBFA",
    surface: "#FFFFFF",
    commentSurface: "#DAF7F4",
    text: "#2A201C",
    textMuted: "#645650",
    border: "#CFE4E1",
  },
  {
    id: "nenyahuang",
    name: "嫩芽黃",
    accent: "#90E8E0",
    link: "#487470",
    wash: "#DAEFCF",
    background: "#F5FBF2",
    surface: "#FFFFFF",
    commentSurface: "#E9F6E3",
    text: "#2B3736",
    textMuted: "#5F6F6E",
    border: "#D9E2D4",
  },
  {
    id: "canglang",
    name: "蒼浪",
    accent: "#ACD6CC",
    link: "#596F6A",
    wash: "#F8E8C6",
    background: "#FDF9EF",
    surface: "#FFFFFF",
    commentSurface: "#FBF1DD",
    text: "#2E3433",
    textMuted: "#666E6C",
    border: "#E6DFD0",
  },
] as const;

export const DEFAULT_PALETTE_ID = "yutanqing";

export function getPalette(id: string | undefined): Palette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0]!;
}

// ---------------------------------------------------------------------------
// 對比度（WCAG 2.2 相對亮度）
// ---------------------------------------------------------------------------

export function parseHex(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`不是合法的 6 位十六進位色：${hex}`);
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 對比度，1..21 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
